import React, {useEffect} from 'react'
import Container from '../../Block/Container'
import {BlockComponentProps} from '../index'
import {useSection} from '../../Section'
import {Font} from '../../lib/font'
import CustomerDetailStep from './CustomerDetailStep'
import Step from './Step'
import BillingAddressStep from './BillingAddressStep'
import ShippingAddressStep from './ShippingAddressStep'
import OrderConfirmationStep from './OrderConfirmationStep'
import PurchaseFormProvider, {usePurchaseForm} from './PurchaseFormProvider'
import PurchaseFormTextField from './PurchaseFormTextField'
import PurchaseFormButton from './PurchaseFormButton'
import {BlockBase} from '../base'
import {Column} from '../../lib/layout'
import PurchaseFormStripe from '../../PurchaseFormStripe'
import styles from './PurchaseForm.module.css'
import {usePurchase} from '../../PurchaseContext'

export const ADDRESS_TYPE_BILLING = 'billing'
export const ADDRESS_TYPE_SHIPPING = 'shipping'
export type AddressType =
  | typeof ADDRESS_TYPE_BILLING
  | typeof ADDRESS_TYPE_SHIPPING

export interface AttendeeCollectionProps {
  active: boolean
  backClick?: () => void
  config: PurchaseFormBlock
  continueClick: () => void
  disabled?: boolean
}

interface PurchaseFormProps extends PurchaseFormBlock {
  TextFieldComponent?: React.FC<any>
  ButtonComponent?: React.FC<any>
}

export type PlaceholderType =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'billingAddress1'
  | 'billingAddress2'
  | 'city'
  | 'state'
  | 'zipcode'
  | 'country'

export interface PurchaseFormBlock extends BlockBase {
  type: 'PurchaseForm'
  isHidden: boolean
  input: {
    textColor: string
    fontSize: number
    backgroundColor: string
    spacing: number
    border: {
      width: number
      radius: number
      color: string
    }
    padding: {
      left: number
      right: number
      top: number
      bottom: number
    }
    placeholder: {
      firstName: string
      lastName: string
      email: string
      phone: string
      billingAddress1: string
      billingAddress2: string
      city: string
      state: string
      zipcode: string
      country: string
      shippingAddress1: string
      shippingAddress2: string
      shippingcity: string
      shippingstate: string
      shippingzipcode: string
      shippingcountry: string
    }
  }
  nextStepButton: {
    text: string
    icon?: string | null
    iconAlign?: 'left' | 'right'
    image?: string
  }
  purchaseButton: {
    text: string
    icon?: string | null
    iconAlign?: 'left' | 'right'
    image?: string
  }
  buttonStyle: {
    size: Column
    font: Font | null
    fontSize?: number
    backgroundColor?: string
    hoverBackgroundColor?: string
    textColor?: string
    hoverTextColor?: string
    borderRadius?: number
    borderWidth?: number
    borderColor?: string
    hoverBorderColor?: string
    height?: number
    width?: number
    disabledBackgroundColor?: string
    disabledTextColor?: string
  }
  buttonsAlignment: 'flex-start' | 'center' | 'flex-end'
  summary: {
    label: {
      attendeeDetailsLabel: string
      billingAddressLabel: string
      shippingAddressLabel: string
      purchaseLabel: string
      totalLabel: string
      color: string
      fontSize: number
    }
    text: {
      color: string
      fontSize: number
    }
  }
  errors: {
    requiredField: string
  }
  questions: {
    sameShippingAddress: string
  }
}

export default function PurchaseForm(
  props: PurchaseFormProps & BlockComponentProps,
) {
  const {stripeAccountId, stripePublicKey} = usePurchase()

  return (
    <PurchaseFormStripe
      stripeAccountId={stripeAccountId ?? ''}
      stripePublicKey={stripePublicKey}
    >
      <PurchaseFormProvider config={props}>
        <Content {...props} />
      </PurchaseFormProvider>
    </PurchaseFormStripe>
  )
}

function Content(props: PurchaseFormProps & BlockComponentProps) {
  const {
    TextFieldComponent = PurchaseFormTextField,
    ButtonComponent = PurchaseFormButton,
  } = props
  const {calculateVisibility} = useSection()
  const {step, setStep, onSubmit, useSameAddressForShipping} = usePurchaseForm()
  const {selectedTicket} = usePurchase()

  // update block height whenever the step changes because certain
  // steps require more space.
  useEffect(calculateVisibility, [calculateVisibility, step])

  return (
    <Container {...props}>
      <div className={styles.contentBox}>
        <Step
          showing={step === 'customer_details'}
          verticalAlignment={props.alignment.vertical}
          padding={props.padding}
          border={props.border}
        >
          <CustomerDetailStep
            TextFieldComponent={TextFieldComponent}
            ButtonComponent={ButtonComponent}
            {...props}
            onContinue={onSubmit}
          />
        </Step>

        {selectedTicket?.billing_address_enabled && (
          <Step
            showing={step === 'address_billing'}
            verticalAlignment={props.alignment.vertical}
            padding={props.padding}
            border={props.border}
          >
            <BillingAddressStep
              TextFieldComponent={TextFieldComponent}
              ButtonComponent={ButtonComponent}
              onBack={() => setStep('customer_details')}
              {...props}
              onContinue={onSubmit}
            />
          </Step>
        )}

        {selectedTicket?.shipping_address_enabled && (
          <Step
            showing={step === 'address_shipping'}
            verticalAlignment={props.alignment.vertical}
            padding={props.padding}
            border={props.border}
          >
            <ShippingAddressStep
              {...props}
              TextFieldComponent={TextFieldComponent}
              ButtonComponent={ButtonComponent}
              onBack={() =>
                selectedTicket?.billing_address_enabled
                  ? setStep('address_billing')
                  : setStep('customer_details')
              }
              onContinue={onSubmit}
            />
          </Step>
        )}

        <Step
          showing={step === 'order_confirmation'}
          verticalAlignment={props.alignment.vertical}
          padding={props.padding}
          border={props.border}
        >
          <OrderConfirmationStep
            {...props}
            ButtonComponent={ButtonComponent}
            onBack={() => {
              if (
                selectedTicket?.shipping_address_enabled &&
                !useSameAddressForShipping
              ) {
                setStep('address_shipping')
                return
              }

              if (selectedTicket?.billing_address_enabled) {
                setStep('address_billing')
                return
              }

              if (selectedTicket?.shipping_address_enabled) {
                setStep('address_shipping')
                return
              }

              setStep('customer_details')
            }}
          />
        </Step>
      </div>
    </Container>
  )
}
